export type ChartScanActivity = {
  activityId: number
  startTime: string
  endTime: string
  type: number
  manualEntry: boolean
  ferryTrainCrossing: boolean
  crew: boolean
}

export type ChartScanViolations = {
  switchViolation: boolean
  multipleChartsUsed: boolean
  dayExceededViolation: boolean
  notSupportedChart: boolean
  dirtyChart: boolean
  activitiesPeriodViolation: boolean
  nameOrSurnameViolation: boolean
  registrationNumberViolation: boolean
  startPlaceViolation: boolean
  endPlaceViolation: boolean
  startDateViolation: boolean
  endDateViolation: boolean
  odometerStartViolation: boolean
  odometerEndViolation: boolean
  timeMismatchViolation: boolean
  beginCountryViolation: boolean
  endCountryViolation: boolean
  missingActivitiesViolation: boolean
}

export const EMPTY_VIOLATIONS: ChartScanViolations = {
  switchViolation: false,
  multipleChartsUsed: false,
  dayExceededViolation: false,
  notSupportedChart: false,
  dirtyChart: false,
  activitiesPeriodViolation: false,
  nameOrSurnameViolation: false,
  registrationNumberViolation: false,
  startPlaceViolation: false,
  endPlaceViolation: false,
  startDateViolation: false,
  endDateViolation: false,
  odometerStartViolation: false,
  odometerEndViolation: false,
  timeMismatchViolation: false,
  beginCountryViolation: false,
  endCountryViolation: false,
  missingActivitiesViolation: false,
}

export type ChartScanCountryChange = {
  countryChangeId: number
  country: string
  previousCountry: string
  atTime: string
}

// One row per vehicle used during the day. The first row covers from dayStart
// until the first change; each subsequent row covers from its startTime to the
// next change (or dayEnd).
export type ChartScanVehicleUsed = {
  vehiclesUsedId: number
  vehicleId?: number | null
  startTime: string
  endTime: string
  odometerStart: number
  odometerEnd: number
  distanceFromChart: number
}

// Speed-trace circle + per-driving-tick distance points; coordinates in canonical
// 800×800 chart space (same as the disc PNG the canvas renders).
export type ChartScanDistancePoint = { x: number; y: number }
export type ChartScanDistanceData = {
  circleCenterX: number
  circleCenterY: number
  circleRadius: number
  startPointsRadius: number
  endPointsRadius: number
  startPoints: ChartScanDistancePoint[]
  endPoints: ChartScanDistancePoint[]
}

export type ChartScanDisc = {
  discId: number
  centerX: number
  centerY: number
  radius: number
  angleDegrees: number
  driverId?: number | null
  vehicleId?: number | null
  dayStartDate: string
  dayEndDate: string
  beginCountry?: number | null
  endCountry?: number | null
  status: 'Completed' | 'Ignored' | 'Deleted'
  noonShiftMinutes: number
  firstVehicleOdometerStart: number
  firstVehicleOdometerEnd: number
  distanceFromChart: number
  tachographStatus: number
  isCrewWork: boolean
  isClosed: boolean
  addManualEntryOnDayStart: boolean
  addManualEntryOnDayEnd: boolean
  dayNote: string
  violations: ChartScanViolations
  activities: ChartScanActivity[]
  distanceData: ChartScanDistanceData
  // Additional vehicles used (vehicle changes after the first); the first
  // vehicle's odometer/distance live in the firstVehicleOdometer* + distanceFromChart
  // fields above.
  vehiclesUsed: ChartScanVehicleUsed[]
  countryChanges: ChartScanCountryChange[]
}

export type ChartScanListFilter = 'Pending' | 'Completed' | 'All'

export type ChartScanListItem = {
  scanId: number
  fileName: string
  uploadedAt: string | null
  uploadedBy: string | null
  discCount: number
  discs: {
    discId: number
    discNumber: number
    status: 'Completed' | 'Ignored'
    chartDate: string | null
    driverName: string | null
    vehicleRegistration: string | null
  }[]
}

export type ChartScanList = {
  totalFiles: number
  items: ChartScanListItem[]
}

export type ChartScanDetail = {
  scanId: number
  fileName: string
  imageWidth: number
  imageHeight: number
  status: 'ready' | 'deleted'
  // Scan-level assigned data (1:1 with WPF SetTachographChartsAssignedData).
  // Set by the pre-assign step; defaults all Ignored discs.
  chartDate: string | null
  driverId: number | null
  vehicleId: number | null
  dontReadActivities: boolean
  // Nothing detected because the scan is below the ~300 dpi the reader needs;
  // lowResolutionFactor says how many times too small the discs are.
  lowResolution?: boolean
  lowResolutionFactor?: number | null
  discs: ChartScanDisc[]
}

export type AssignChartDiscItem = {
  discId: number
  chartDate: string         // ISO date — only the date part is used server-side
  driverId: number | null
  vehicleId: number | null
  dontReadActivities: boolean
}

export type AssignChartScanPayload = {
  discs: AssignChartDiscItem[]
}

export type ChartScanUploadResponse = {
  scanId: number
  status: string
  discsDetected: number
  detail: ChartScanDetail
}

export type UpdateDiscConflict = {
  startTime: string
  endTime: string
  source: number
}

export type UpdateDiscResponse = {
  saved: boolean
  conflicts: UpdateDiscConflict[]
  warnings: string[]
}

export type UpdateDiscPayload = {
  driverId?: number | null
  vehicleId?: number | null
  dayStartDate: string
  dayEndDate: string
  beginCountry?: number | null
  endCountry?: number | null
  noonShiftMinutes: number
  firstVehicleOdometerStart: number
  firstVehicleOdometerEnd: number
  distanceFromChart: number
  tachographStatus: number
  isCrewWork: boolean
  isClosed: boolean
  addManualEntryOnDayStart: boolean
  addManualEntryOnDayEnd: boolean
  dayNote: string
  violations: ChartScanViolations
  activities: Omit<ChartScanActivity, 'activityId'>[]
  additionalVehiclesUsed: Omit<ChartScanVehicleUsed, 'vehiclesUsedId'>[]
  countryChanges: Omit<ChartScanCountryChange, 'countryChangeId'>[]
  // Edited distance points (red start / cyan end dots) plus their ring geometry.
  // The server wipes-and-rewrites the persisted distance-point rows from this set.
  distanceData: ChartScanDistanceData
  // false = run overlap check first; true = bypass it (after user confirmed YesNo).
  force?: boolean
}

// Per-minute diagnostic dump from the server. Used in the editor's diagnostic
// overlay to show, minute by minute, what pixel run length the radial scan
// produced and which activity type that maps to.
export type ChartScanDiagnostic = {
  discId: number
  centerX: number
  centerY: number
  radius: number
  minutesToActivitySize: number[]
  minutesToActivityType: number[]
  mergedActivities: ChartScanActivity[]
}

export const ActivityType = {
  Rest: 0,
  Availability: 1,
  Work: 2,
  Driving: 3,
  NoData: 4,
} as const

export type ActivityTypeId = (typeof ActivityType)[keyof typeof ActivityType]
